'use client'

import { useCallback, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Bell } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/shadcn/popover'
import { Button } from '@/components/ui/shadcn/button'
import { Badge } from '@/components/ui/shadcn/badge'
import { cn } from '@/lib/utils'

export default function NotificationBell() {
  const authContext = useContext(AuthContext)
  const router = useRouter()
  const orgId = authContext?.user?.organization?.id
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState([])
  const [open, setOpen] = useState(false)

  const loadCount = useCallback(async () => {
    if (!orgId) return
    try {
      const { data } = await axios(
        `/api/admin/organization/${orgId}/notifications/unread-count`,
        { method: 'GET' },
        authContext,
      )
      setUnread(data?.count || 0)
    } catch {
      /* best-effort */
    }
  }, [orgId, authContext])

  const loadList = useCallback(async () => {
    if (!orgId) return
    try {
      const { data } = await axios(
        `/api/admin/organization/${orgId}/notifications?limit=20`,
        { method: 'GET' },
        authContext,
      )
      setItems(data?.notifications || [])
    } catch {
      /* best-effort */
    }
  }, [orgId, authContext])

  useEffect(() => {
    loadCount()
    const t = setInterval(loadCount, 30000)
    return () => clearInterval(t)
  }, [loadCount])

  useEffect(() => {
    if (open) loadList()
  }, [open, loadList])

  const onItemClick = async n => {
    try {
      await axios(
        `/api/admin/organization/${orgId}/notifications/${n.id}/read`,
        { method: 'PATCH' },
        authContext,
      )
    } catch {
      /* best-effort */
    }
    setOpen(false)
    setUnread(u => Math.max(0, u - (n.read ? 0 : 1)))
    if (n.driftEventId) router.push(`/manager/drift/${n.driftEventId}`)
  }

  const markAll = async () => {
    try {
      await axios(
        `/api/admin/organization/${orgId}/notifications/read-all`,
        { method: 'PATCH' },
        authContext,
      )
    } catch {
      /* best-effort */
    }
    setUnread(0)
    setItems(prev => prev.map(n => ({ ...n, read: true })))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 h-4 min-w-4 justify-center px-1 text-[10px]"
              data-testid="notif-unread-badge"
            >
              {unread > 99 ? '99+' : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Notifications</span>
          {items.some(n => !n.read) && (
            <button
              type="button"
              onClick={markAll}
              className="text-xs text-muted-foreground hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No notifications
            </p>
          ) : (
            items.map(n => (
              <button
                key={n.id}
                type="button"
                onClick={() => onItemClick(n)}
                className={cn(
                  'flex w-full flex-col gap-0.5 border-b px-3 py-2 text-left hover:bg-accent/40',
                  !n.read && 'bg-accent/20',
                )}
              >
                <span className="line-clamp-1 text-sm font-medium">
                  {n.title}
                </span>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {n.severity}
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
